import { Meta, StoryFn, StoryObj } from '@storybook/react';
import {
  SmartContentDisplayStory,
  SmartContentDisplayStoryProps,
} from './SmartContentDisplayStory';

const meta: Meta<SmartContentDisplayStoryProps> = {
  title: 'Display/SmartContentDisplay',
  component: SmartContentDisplayStory,
  parameters: {
    docs: {
      description: {
        component:
          'A smart content display component that automatically truncates long content and shows a gradient overlay with expand/collapse functionality.',
      },
    },
  },
  argTypes: {
    maxHeight: {
      name: 'Max Height',
      description: 'Maximum height before content is truncated (in pixels)',
      control: { type: 'number', min: 100, max: 500, step: 25 },
    },
    minDelta: {
      name: 'Min Delta',
      description:
        'Minimum height difference required to show expand/collapse functionality (in pixels)',
      control: { type: 'number', min: 10, max: 200, step: 10 },
    },
    expandText: {
      name: 'Expand Text',
      description: 'Text displayed on the expand button',
      control: { type: 'text' },
    },
    collapseText: {
      name: 'Collapse Text',
      description: 'Text displayed on the collapse button',
      control: { type: 'text' },
    },
    contentType: {
      name: 'Content Type',
      description: 'Type of content to display (affects length and complexity)',
      control: { type: 'select' },
      options: ['short', 'medium', 'long', 'very-long'],
    },
  },
  args: {
    maxHeight: 250,
    minDelta: 50,
    expandText: 'Show more',
    collapseText: 'Show less',
    contentType: 'long',
  },
};

export default meta;
type Story = StoryObj<typeof meta>;

const Template: StoryFn<SmartContentDisplayStoryProps> = args => {
  return <SmartContentDisplayStory {...args} />;
};

export const Default: Story = Template.bind({});

export const ShortContent: Story = Template.bind({});
ShortContent.args = {
  contentType: 'short',
  maxHeight: 200,
};

export const MediumContent: Story = Template.bind({});
MediumContent.args = {
  contentType: 'medium',
  maxHeight: 200,
};

export const LongContent: Story = Template.bind({});
LongContent.args = {
  contentType: 'long',
  maxHeight: 250,
};

export const VeryLongContent: Story = Template.bind({});
VeryLongContent.args = {
  contentType: 'very-long',
  maxHeight: 300,
};

export const SmallContainer: Story = Template.bind({});
SmallContainer.args = {
  maxHeight: 150,
  contentType: 'long',
};

export const LargeContainer: Story = Template.bind({});
LargeContainer.args = {
  maxHeight: 400,
  contentType: 'very-long',
};

export const CustomButtonText: Story = Template.bind({});
CustomButtonText.args = {
  expandText: 'Read more...',
  collapseText: 'Read less...',
  contentType: 'long',
  maxHeight: 250,
};

export const MinDeltaDemo: Story = Template.bind({});
MinDeltaDemo.args = {
  contentType: 'medium',
  maxHeight: 200,
  minDelta: 100,
};
MinDeltaDemo.parameters = {
  docs: {
    description: {
      story:
        'This story demonstrates the minDelta prop. With minDelta set to 100px, the content will only show the expand/collapse functionality when the height difference exceeds 100px. Try adjusting the minDelta value to see how it affects the behavior.',
    },
  },
};
